// Canonical birthdate handling for the kiosk.
//
// The birthdate step displays and accepts a date in a venue-configured field
// order (DD / MM / YYYY or MM / DD / YYYY). To keep that ambiguity out of the
// rest of the app, the typed string is parsed into a `CalendarDate` value
// object — named fields, no timezone, JSON-safe — as soon as it is complete and
// valid. Every downstream consumer (validity, minimum-age gate, age calculation)
// reads the `CalendarDate` and never needs to know the display order.

// Field order the birthdate input renders. The value doubles as the input's
// placeholder template, so it is spelled out in full rather than a code.
export type DateFormat = 'DD / MM / YYYY' | 'MM / DD / YYYY';

// A plain calendar date (month is 1-12). Deliberately not a JS `Date`: a `Date`
// is an instant with a timezone, which would shift the day when the user object
// is JSON-serialized to the submit endpoint.
export type CalendarDate = {year: number; month: number; day: number};

// Parses the typed buffer into a calendar date according to the venue's field
// order. Returns null when the string isn't three numeric parts — i.e. while
// the visitor is still typing. Does not range-check; pair with isValidBirthdate.
export function parseBirthdate(input: string, format: DateFormat): CalendarDate | null {
  const parts = input.split('/').map(part => part.trim());
  if (parts.length !== 3) return null;

  const numbers = parts.map(part => parseInt(part, 10));
  if (numbers.some(Number.isNaN)) return null;

  const [first, second, year] = numbers;
  const [day, month] = format === 'MM / DD / YYYY' ? [second, first] : [first, second];
  return {year, month, day};
}

// Inverse of parseBirthdate: renders a stored calendar date back into the
// display buffer for the given format (used when the visitor returns to the
// step via back navigation or refresh).
export function formatBirthdate(date: CalendarDate, format: DateFormat): string {
  const day = String(date.day).padStart(2, '0');
  const month = String(date.month).padStart(2, '0');
  const year = String(date.year);
  return format === 'MM / DD / YYYY'
    ? `${month} / ${day} / ${year}`
    : `${day} / ${month} / ${year}`;
}

// Whether a calendar date is a complete, plausible birthdate: real Y/M/D (e.g.
// rejects Feb 30), not in the future, and within the last 120 years. Order-free
// — the fields are already named. Shared between the birthdate page and the
// flow's stage-validity check so both agree on what counts as valid.
export function isValidBirthdate(date: CalendarDate | undefined): boolean {
  if (!date) return false;

  const {year, month, day} = date;

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Catch invalid dates (e.g. Feb 30) by round-tripping through Date.
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Reject future dates
  if (parsed > today) return false;

  // Enforce maximum age of 120 years
  const minDate = new Date(today);
  minDate.setFullYear(today.getFullYear() - 120);
  return parsed >= minDate;
}
